import {
  ScenariosCondition,
  ScenariosRequest,
  ScenariosResponse,
  scenario,
} from '@api/api/scenario';
import { RangeHorizon } from '@helpers/utils/dates/dates';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { QueryClient, queryOptions } from '@tanstack/react-query';
import { config } from '@terminal/config';
import { withGcTime } from '@terminal/services/query-client/utils';
import {
  ApiStreamProps,
  apiStream,
} from '@terminal/utils/api-stream/apiStream';
import { formatScenarioConditionValue } from '@terminal/utils/condition-utils/condition-utils';

export const QUERY_KEY_SCENARIOS = 'QUERY_KEY_SCENARIOS';
export const SCENARIO_HORIZONS: string[] = [
  RangeHorizon.OneWeek,
  RangeHorizon.TwoWeeks,
  RangeHorizon.OneMonth,
  RangeHorizon.ThreeMonths,
  RangeHorizon.SixMonths,
  RangeHorizon.OneYear,
];

export const createScenarioRequest = ({
  conditions,
  entityTags,
}: {
  conditions: ScenariosCondition[];
  entityTags: string[];
}): ScenariosRequest => ({
  entities: entityTags.map(tag => ({ tag })),
  conditions: conditions.map(formatScenarioConditionValue),
  horizons: SCENARIO_HORIZONS,
  start_horizon: RangeHorizon.ZeroDay,
  end_horizon: RangeHorizon.OneYear,
  skip_report: false,
  non_overlapping: true,
});

interface CreateScenariosQueryOptionsProps {
  body: ScenariosRequest;
  queryClient: QueryClient;
  gcTime?: number;
}

export const createScenariosQueryKey = (body: ScenariosRequest) =>
  [
    QUERY_KEY_SCENARIOS,
    {
      ...body,
      ...(body.conditions
        ? {
            conditions: body.conditions.map(
              ({ display: _display, ...condition }) => condition
            ),
          }
        : {}),
    },
  ] as const;

export const createScenariosQueryOptions = ({
  body,
  queryClient,
  gcTime,
}: CreateScenariosQueryOptionsProps) => {
  const queryKey = createScenariosQueryKey(body);

  return withGcTime(
    queryOptions({
      queryKey,
      queryFn: ({ signal }) =>
        new Promise<ScenariosResponse[]>((resolve, reject) => {
          const controller = new AbortController();
          let latestData: ScenariosResponse[] = [];

          const handleAbort = () => controller.abort();
          signal.addEventListener('abort', handleAbort, { once: true });

          const cleanup = () => {
            signal.removeEventListener('abort', handleAbort);
          };

          const streamProps: ApiStreamProps<ScenariosResponse> = {
            body,
            onReadChunk: chunks => {
              signal.throwIfAborted();
              latestData = [...latestData, ...chunks];
              queryClient.setQueryData(queryKey, latestData);
            },
            onComplete: () => {
              cleanup();
              resolve(latestData);
            },
            onError: error => {
              cleanup();
              reject(error ?? new Error('Scenario request failed'));
            },
            onAbort: () => {
              cleanup();
              reject(
                new DOMException('Scenario request aborted', 'AbortError')
              );
            },
            controller,
            endpoint: `${config.api.root}${scenario.scenarios.path}`,
          };

          apiStream(streamProps);
        }),
      staleTime: getTimeTillEndOfDayMs(),
    }),
    gcTime
  );
};
